import { ANALYTICS_SCHEMA_VERSION, type AnalyticsIdentity, type SessionMode, type SessionSnapshot } from './contract';

export interface SessionTrackerOptions {
  identity: AnalyticsIdentity;
  engineVersion: string;
  contentVersion: string;
  startedAt?: Date;
  now?: () => number;
  visible?: boolean;
  idleAfterMs?: number;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
}

const SESSION_TRACKER_STATE_KEY = 'towerarena.com.analytics.session-tracker-state';

interface PersistedSessionTrackerState {
  schemaVersion: 1;
  sessionId: string;
  startedAt: string;
  activeMs: number;
  gameplayMs: number;
  menuMs: number;
  pausedMs: number;
  matchesStarted: number;
  matchesCompleted: number;
  matchesAbandoned: number;
}

function validCounter(value: unknown, integer = false): value is number {
  return typeof value === 'number'
    && Number.isFinite(value)
    && value >= 0
    && value <= Number.MAX_SAFE_INTEGER
    && (!integer || Number.isInteger(value));
}

function restoreState(
  storage: SessionTrackerOptions['storage'],
  sessionId: string,
): PersistedSessionTrackerState | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SESSION_TRACKER_STATE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<PersistedSessionTrackerState>;
    if (value.schemaVersion !== 1
      || value.sessionId !== sessionId
      || typeof value.startedAt !== 'string'
      || !Number.isFinite(Date.parse(value.startedAt))
      || !validCounter(value.activeMs)
      || !validCounter(value.gameplayMs)
      || !validCounter(value.menuMs)
      || !validCounter(value.pausedMs)
      || !validCounter(value.matchesStarted, true)
      || !validCounter(value.matchesCompleted, true)
      || !validCounter(value.matchesAbandoned, true)) return null;
    return value as PersistedSessionTrackerState;
  } catch {
    return null;
  }
}

export class SessionTracker {
  private readonly now: () => number;
  private readonly startedAt: string;
  private readonly idleAfterMs: number;
  private readonly storage: SessionTrackerOptions['storage'];
  private lastSampleMs: number;
  private lastInteractionMs: number;
  private visible: boolean;
  private mode: SessionMode = 'menu';
  private activeMs = 0;
  private gameplayMs = 0;
  private menuMs = 0;
  private pausedMs = 0;
  private matchesStarted = 0;
  private matchesCompleted = 0;
  private matchesAbandoned = 0;

  constructor(private readonly options: SessionTrackerOptions) {
    this.now = options.now ?? (() => performance.now());
    this.storage = options.storage;
    const restored = restoreState(this.storage, options.identity.sessionId);
    this.startedAt = restored?.startedAt ?? (options.startedAt ?? new Date()).toISOString();
    this.idleAfterMs = options.idleAfterMs ?? 60_000;
    this.visible = options.visible ?? true;
    this.lastSampleMs = this.now();
    this.lastInteractionMs = this.lastSampleMs;
    if (restored) {
      this.activeMs = restored.activeMs;
      this.gameplayMs = restored.gameplayMs;
      this.menuMs = restored.menuMs;
      this.pausedMs = restored.pausedMs;
      this.matchesStarted = restored.matchesStarted;
      this.matchesCompleted = restored.matchesCompleted;
      this.matchesAbandoned = restored.matchesAbandoned;
    }
  }

  noteInteraction(): void {
    const now = this.now();
    this.sample(now);
    this.lastInteractionMs = now;
  }

  setVisible(visible: boolean): void {
    const now = this.now();
    this.sample(now);
    this.visible = visible;
    if (visible) this.lastInteractionMs = now;
  }

  setMode(mode: SessionMode): void {
    const now = this.now();
    this.sample(now);
    this.mode = mode;
  }

  matchStarted(): void {
    this.matchesStarted += 1;
    this.setMode('gameplay');
    this.persist();
  }

  matchCompleted(): void {
    this.sample(this.now());
    this.matchesCompleted += 1;
    this.mode = 'menu';
    this.persist();
  }

  matchAbandoned(): void {
    this.sample(this.now());
    this.matchesAbandoned += 1;
    this.mode = 'menu';
    this.persist();
  }

  snapshot(occurredAt: Date = new Date()): SessionSnapshot {
    this.sample(this.now());
    this.persist();
    return {
      schemaVersion: ANALYTICS_SCHEMA_VERSION,
      anonymousId: this.options.identity.anonymousId,
      sessionId: this.options.identity.sessionId,
      startedAt: this.startedAt,
      occurredAt: occurredAt.toISOString(),
      activeSeconds: Math.floor(this.activeMs / 1000),
      gameplaySeconds: Math.floor(this.gameplayMs / 1000),
      menuSeconds: Math.floor(this.menuMs / 1000),
      pausedSeconds: Math.floor(this.pausedMs / 1000),
      matchesStarted: this.matchesStarted,
      matchesCompleted: this.matchesCompleted,
      matchesAbandoned: this.matchesAbandoned,
      engineVersion: this.options.engineVersion,
      contentVersion: this.options.contentVersion,
    };
  }

  private persist(): void {
    if (!this.storage) return;
    const state: PersistedSessionTrackerState = {
      schemaVersion: 1,
      sessionId: this.options.identity.sessionId,
      startedAt: this.startedAt,
      activeMs: this.activeMs,
      gameplayMs: this.gameplayMs,
      menuMs: this.menuMs,
      pausedMs: this.pausedMs,
      matchesStarted: this.matchesStarted,
      matchesCompleted: this.matchesCompleted,
      matchesAbandoned: this.matchesAbandoned,
    };
    try { this.storage.setItem(SESSION_TRACKER_STATE_KEY, JSON.stringify(state)); }
    catch { /* Analytics remains best-effort when browser storage is unavailable. */ }
  }

  private sample(now: number): void {
    const delta = Math.max(0, Math.min(now - this.lastSampleMs, 5 * 60_000));
    this.lastSampleMs = now;
    if (!this.visible || delta === 0) return;
    if (this.mode === 'gameplay') {
      this.activeMs += delta;
      this.gameplayMs += delta;
      return;
    }
    if (this.mode === 'paused') {
      this.activeMs += delta;
      this.pausedMs += delta;
      return;
    }
    if (now - this.lastInteractionMs <= this.idleAfterMs) {
      this.activeMs += delta;
      this.menuMs += delta;
    }
  }
}
